import React, { FC, useContext } from "react";
import { GridLegacy as Grid } from "@mui/material";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";
import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupContext, EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import { IPreferences } from "../../logic/api";
import { ReleaseFlag, useFeatureVariant } from "../../utility/feature-management";
import { SessionContext } from "../../utility/useSession";
import styles from "./styles";
import { useTranslation } from "react-i18next";

export interface WorkflowRuleSettingsProps {
    onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

const BusCheckoutSelfParkOnlyField: FC = () => {
    const { t } = useTranslation();
    const editableGroup = useContext(EditableGroupContext);
    const data = editableGroup.data as IPreferences | undefined;

    // Only relevant while Bus Check Out is switched on
    if (data?.workflowRules?.mobileBusCheckoutEnabled != true) {
        return null;
    }

    return <EditableField<IPreferences, boolean>
        inputType={EditableFieldTypes.switch}
        label={t("settings.mobileBusCheckOutSelfParkOnly")}
        onProvide={(data) => {
            return data.workflowRules?.mobileBusCheckoutSelfParkOnly == true;
        }}
        onChange={(data, value) => {
            if (data.workflowRules) {
                data.workflowRules.mobileBusCheckoutSelfParkOnly = value;
            }
        }}
        testId="settings-mobile-bus-checkout-self-park-only-switch"
    />
}

export const WorkflowRuleSettings: FC<WorkflowRuleSettingsProps> = ({ onSave }) => {
    const session = useContext(SessionContext);
    const site = session.current.site;
    const { t } = useTranslation();

    if (!site) {
        return null;
    }

    const preferences = site.preferences;
    const allowCheckoutFromBlockParkingEnabled = useFeatureVariant(ReleaseFlag.releaseAllowCheckoutFromBlockParking, false);
    const allowVehicleInReturnsBeforeKeyEnabled = useFeatureVariant(ReleaseFlag.releaseVehicleInReturnsBeforeKey, false);
    const isKeyThenLocationScanningReleased = useFeatureVariant(ReleaseFlag.releaseKeyThenLocationScanning, false);
    const isDisableVehicleScanToUserReleased = useFeatureVariant(ReleaseFlag.releaseDisableVehicleScanToUser, false);
    const isMobileBusCheckOutReleased = useFeatureVariant(ReleaseFlag.releaseMobileBusCheckout, false);
    const isMobileBusCheckOutSelfParkOnlyReleased = useFeatureVariant(ReleaseFlag.releaseMobileBusCheckoutSelfParkOnly, false);
    const releaseAdditionalExternalReferences = useFeatureVariant(ReleaseFlag.releaseAdditionalExternalReferences, false);

    const setAdminOnlyCheckout = (data: IPreferences, value: boolean) => {
        if (allowCheckoutFromBlockParkingEnabled && data.workflowRules) {
            data.workflowRules.bookingCheckoutAdminEnabled = value;
        } else {
            data.booking_checkout_admin_enabled = value;
        }
    }

    const setAllowCheckoutWithoutCheckin = (data: IPreferences, value: boolean) => {
        if (allowCheckoutFromBlockParkingEnabled && data.workflowRules) {
            data.workflowRules.bookingCheckoutWithoutCheckinEnabled = value;
        } else {
            data.booking_checkout_without_checkin_enabled = value;
        }
    }

    return <Grid item xs={12}>
        <NotchedCard label={t("settings.workflowRules")} style={styles.card}>
            <EditableGroup<IPreferences>
                id="workflowRules"
                testId="workflow-rules-group"
                editType={EditableGroupType.switchableEditing}
                data={preferences}
                onDataChanged={(data) => onSave({
                    scan_key_to_checkout: data.scan_key_to_checkout,
                    booking_checkout_admin_enabled: data.booking_checkout_admin_enabled,
                    booking_checkout_without_checkin_enabled: data.booking_checkout_without_checkin_enabled,
                    keymovements_autoassign: data.keymovements_autoassign,
                    keymovements_key_then_location: data.keymovements_key_then_location,
                    workflowRules: data.workflowRules
                })}
            >
                <Grid container>
                    <EditableField<IPreferences, boolean>
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.scanKeyCheckout")}
                        onProvide={(data) => data.scan_key_to_checkout == true}
                        onChange={(data, value) => { data.scan_key_to_checkout = value }}
                        testId="settings-scan-key-checkout-switch"
                    />

                    <EditableField<IPreferences, boolean>
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.adminOnlyCheckout")}
                        onProvide={(data) => data.workflowRules?.bookingCheckoutAdminEnabled ?? data.booking_checkout_admin_enabled}
                        onChange={setAdminOnlyCheckout}
                        testId="settings-admin-only-checkout-switch"
                    />

                    {allowCheckoutFromBlockParkingEnabled && (
                        <EditableField<IPreferences, boolean>
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.blockParkAllowCheckout")}
                            onProvide={(data) =>
                                data.workflowRules?.bookingCheckoutAllowFromBlockPark ?? true
                            }
                            onChange={(data, value) => {
                                if (data.workflowRules) {
                                    data.workflowRules.bookingCheckoutAllowFromBlockPark = value;
                                }
                            }}
                            testId="settings-block-park-allow-checkout-switch"
                        />
                    )}
                    <EditableField<IPreferences, boolean>
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.allowCheckInWithoutCheckIn")}
                        onProvide={(data) => data.workflowRules?.bookingCheckoutWithoutCheckinEnabled ?? data.booking_checkout_without_checkin_enabled}
                        onChange={setAllowCheckoutWithoutCheckin}
                        testId="settings-allow-checkout-without-checkin-switch"
                    />

                    <EditableField<IPreferences, boolean>
                        inputType={EditableFieldTypes.switch}
                        label={t("settings.autoAssign")}
                        onProvide={(data) => data.keymovements_autoassign}
                        onChange={(data, value) => data.keymovements_autoassign = value}
                        testId="settings-auto-assign-switch"
                    />

                    {isKeyThenLocationScanningReleased && (
                        <EditableField<IPreferences, boolean>
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.keymovementsKeyThenLocation")}
                            onProvide={(data) => data.keymovements_key_then_location == true}
                            onChange={(data, value) => data.keymovements_key_then_location = value}
                            testId="settings-keymovements-key-then-location-switch"
                        />
                    )}

                    {allowVehicleInReturnsBeforeKeyEnabled && (
                        <EditableField<IPreferences, boolean>
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.vehiclesInReturnsBeforeKey")}
                            onProvide={(data) => {
                                return data.workflowRules?.vehicleInReturnsBeforeKey ?? true;
                            }}
                            onChange={(data, value) => {
                                if (data.workflowRules) {
                                    data.workflowRules.vehicleInReturnsBeforeKey = value;
                                }
                            }}
                            testId="settings-vehicles-in-returns-before-key-switch"
                        />
                    )}

                    {isDisableVehicleScanToUserReleased && (
                        <EditableField<IPreferences, boolean>
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.preventVehicleScanToUser")}
                            onProvide={(data) => {
                                return data.workflowRules?.preventVehicleScanToUser ?? true;
                            }}
                            onChange={(data, value) => {
                                if (data.workflowRules) {
                                    data.workflowRules.preventVehicleScanToUser = value;
                                }
                            }}
                            testId="settings-prevent-vehicle-scan-to-user-switch"
                        />
                    )}

                    {releaseAdditionalExternalReferences && preferences.use_tracking_id && (
                        <EditableField<IPreferences, boolean>
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.requireTrackingIdOnCheckIn")}
                            onProvide={(data) => {
                                return data.workflowRules?.requireTrackingIdOnCheckIn ?? false;
                            }}
                            onChange={(data, value) => {
                                if (data.workflowRules) {
                                    data.workflowRules.requireTrackingIdOnCheckIn = value;
                                }
                            }}
                            testId="settings-require-tracking-id-on-checkin-switch"
                        />
                    )}

                    {isMobileBusCheckOutReleased && (
                        <EditableField<IPreferences, boolean>
                            inputType={EditableFieldTypes.switch}
                            label={t("settings.mobileBusCheckOut")}
                            onProvide={(data) => {
                                return data.workflowRules?.mobileBusCheckoutEnabled == true;
                            }}
                            onChange={(data, value) => {
                                if (data.workflowRules) {
                                    data.workflowRules.mobileBusCheckoutEnabled = value;
                                }
                            }}
                            testId="settings-mobile-bus-checkout-switch"
                        />
                    )}

                    {isMobileBusCheckOutSelfParkOnlyReleased && <BusCheckoutSelfParkOnlyField />}

                </Grid >
            </EditableGroup >
        </NotchedCard >
    </Grid >
}
